import React, { Dispatch, SetStateAction } from 'react';
import { ConflictKeywordGq } from '../../../../../../graphql/projects/models/ConflictKeywordGq';
import { TagGroupAutoColumnGq } from '../../../../../../graphql/projects/models/TagGroupAutoColumnGq';
import { TagGroupGq } from '../../../../../../graphql/projects/models/TagGroupGq';
import { projectsApiService } from '../../../../../../graphql/projects/projectsApiService';
import VoxelTable from '../../../../../../components/voxel/Tables/VoxelTable';
import VoxelTableHeadCell from '../../../../../../components/voxel/Tables/VoxelTableHeadCell';
import VoxelTableRow from '../../../../../../components/voxel/Tables/VoxelTableRow';
import ConflictsTableRow from './ConflictsTableRow';
import ConflictsTagFilter from './ConflictsTagFilter';
import { useConflictDragBag } from './logic/useConflictDragBag';
import { ConflictTableState } from './logic/useConflictTableState';
import VoxelTableCheckbox from '../../../../../../components/voxel/Forms/VoxelTableCheckbox';
import VoxelTableHeadFilterCell from '../../../../../../components/voxel/Tables/VoxelTableHeadFilterCell';
import VoxelTableHeadSearchCell from '../../../../../../components/voxel/Tables/VoxelTableHeadSearchCell';
import VoxelAdvancedTextSearch from '../../../../../../components/voxel/Search/VoxelAdvancedTextSearch';
import VoxelTablePagination from '../../../../../../components/voxel/Tables/VoxelTablePagination';
import BulkApplyButton from '../BulkApplyButton';
import BulkSkipButton from '../BulkSkipButton';

export interface ConflictsTableProps {
  tableState: ConflictTableState;
  conflicts: ConflictKeywordGq[];
  tagGroup: TagGroupGq;
  reload: () => Promise<any>;
  totalCount: number;
  amountPerPage: number;
  localPage: number;
  setLocalPage: Dispatch<SetStateAction<number>>;
  areConflictsSelected: boolean;
  bulkSelectedTag: number;
  setBulkSelectedTag: Dispatch<SetStateAction<number>>;
}

const ConflictsTable: React.FC<ConflictsTableProps> = ({
  tableState,
  conflicts,
  tagGroup,
  reload,
  totalCount,
  amountPerPage,
  localPage,
  setLocalPage,
  areConflictsSelected,
  bulkSelectedTag,
  setBulkSelectedTag
}) => {

  const conflictingTagsQuery = projectsApiService.queries.useGetConflictingTags({
    tagGroupId: tagGroup.id,
    keywordQuery: {
      filtering: tableState.backendFilterState,
    },
  });
  const dragBag = useConflictDragBag(tableState, conflicts);

  return (
    <VoxelTable>
      <thead>
        <VoxelTableRow>
          <VoxelTableHeadCell size='sm'>
            <VoxelTableCheckbox
              color='slate'
              checked={tableState.selectionState.invertSelection}
              onChange={() =>
                tableState.setSelectionState((prevState) => ({
                  keywordIds: [],
                  invertSelection: !prevState.invertSelection,
                  emptyIsAll: true,
                }))
              }
            />
          </VoxelTableHeadCell>
          <VoxelTableHeadSearchCell size='md' title={'Keyword'}>
            <VoxelAdvancedTextSearch
              widthClass='w-fit'
              size='xs'
              value={tableState.filterState.keyword.searchTerm ?? ''}
              onChange={(event) => tableState.setFilterState((prev) => ({ ...prev, keyword: { searchTerm: event.target.value } }))}
              placeholder={'Keyword'}
              deleteValue={() => tableState.setFilterState((prev) => ({ ...prev, keyword: { searchTerm: '' } }))}
            />
          </VoxelTableHeadSearchCell>
          {tagGroup.autotagColumn === TagGroupAutoColumnGq.TRANSLATED_FROM_KEYWORD && (
            <VoxelTableHeadCell size='md'>
              Translated from
            </VoxelTableHeadCell>
          )}
          {tagGroup.autotagColumn === TagGroupAutoColumnGq.TRANSLATION_ENGLISH && (
            <VoxelTableHeadCell size='md'>
              English
            </VoxelTableHeadCell>
          )}
          <VoxelTableHeadFilterCell
            size='md'
            title={'Tag suggestions'}
            hasChange={!!tableState.filterState.conflictTagFilters.length}
          >
            <ConflictsTagFilter
              tagGroup={tagGroup}
              tags={conflictingTagsQuery?.conflictingTags}
              tableState={tableState}
            />
          </VoxelTableHeadFilterCell>
          <VoxelTableHeadCell size='md' align='right'>
            <span className='flex flex-row gap-2 justify-end '>
              <BulkApplyButton
                tableState={tableState}
                tagGroup={tagGroup}
                disabled={!areConflictsSelected || bulkSelectedTag === null}
                reload={reload}
                resetSelection={tableState.handleResetSelection}
                bulkSelectedTag={bulkSelectedTag}
                setBulkSelectedTag={setBulkSelectedTag}
              />
              <BulkSkipButton
                tableState={tableState}
                tagGroup={tagGroup}
                disabled={!areConflictsSelected}
                reload={reload}
                resetSelection={tableState.handleResetSelection}
              />
            </span>
          </VoxelTableHeadCell>
        </VoxelTableRow>
      </thead>
      <tbody>
        {conflicts.map((conflict, index) => (
          <ConflictsTableRow
            key={conflict.keywordId}
            tableState={tableState}
            column={tagGroup.autotagColumn}
            conflict={conflict}
            reload={reload}
            projectId={tagGroup.projectId}
            index={index}
            dragSelected={dragBag.isIndexSelected(index)}
            onMouseOver={dragBag.onMouseOver}
            onMouseDown={dragBag.onMouseDown}
            onMouseUp={dragBag.deactivateDragMode}
          />
        ))}
        <VoxelTablePagination
          rowsPerPage={amountPerPage}
          onPageChange={(event, page) => setLocalPage(page)}
          colSpan={4}
          size='md'
          align={'right'}
          currentPage={localPage}
          totalCount={totalCount}
        />
      </tbody>
    </VoxelTable>
  );
};

export default ConflictsTable;
